import { useEffect, useState } from "react";
import useDocumentTitle from "../../utils/useDocumentTitle";

import { Checkbox, FormControlLabel } from "@material-ui/core";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import ClassNames from "classnames";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useLocation } from "react-router-dom";
import packageJson from "../../../package.json";
import Button from "../../components/Button";
import OTPInput from "../../components/Otpinput";
import PasswordField from "../../components/PasswordField";
import QRInput from "../../components/QRinput";
import QRInputDisplay from "../../components/QRinputDisplay";
import TextField from "../../components/TextField";
import FlashCoachLogo from "../../components/_Icons/FlashCoachLogo";
import TDCXLogo from "../../components/_Icons/TDCXLogo";
import { useNotification } from "../../context/notification";
import { getLoginUrl, handleLoginSuccess } from "../../helpers/loginHelper";
import { apiRequest } from "../../utils/api";
import {
  clearTokenFromLocalStorage,
  getLocalValue,
  hasToken
} from "../../utils/auth";
import getUserIP, { formatIPJSON } from "../../utils/getUserIP";
import FirstTimeLoginModal from "./FirstTimeLoginModal";
import styles from "./Login.module.scss";
import MaintenanceBanner from "./MaintenanceBanner";

export function Login(props) {
  useDocumentTitle(props.title);
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const { addNotification } = useNotification();
  const [showOTPScreen, setShowOTPScreen] = useState(false);
  const [showQRScreen, setShowQRScreen] = useState(false);
  const [userID, setUserID] = useState(null);
  const [userIP, setUserIP] = useState(null);
  const [numberOfAvailalbeTries, setNumberOfAvailalbeTries] = useState(10);
  const [scanDone, setScanDone] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [verificationAttempted, setVerificationAttempted] = useState(false);
  const [autoOtp, setAutoOtp] = useState("")
  const [hasCompletedSecurityQuestions, setHasCompletedSecurityQuestions] = useState(0);
  const [isQRReset, setIsQRReset] = useState(false);
  const [showFirstTimeModal, setShowFirstTimeModal] = useState(false);
  const [securityResetToken, setSecurityResetToken] = useState(null);
  // separate attempts counter for the QR reset OTP flow - does not touch user_login table
  const [qrResetAttempts, setQrResetAttempts] = useState(5);
  const helpRequestButtonPhrase = "Help Request Form";
  const params = new URLSearchParams(window.location.search)
  const autoLoginId = params.get('id')
  const autoLoginOtp = params.get('otp')

  const [loginForm, setLoginForm] = useState({
    username: "",
    password: "",
    remember: false,
    showPassword: false,
  });

  const handleLoginFormEventChange = prop => event => {
    setLoginForm({
      ...loginForm,
      [prop]: event.target.value,
    });
  };

  const handleLoginFormChange = prop => value => {
    setLoginForm({ ...loginForm, [prop]: value });
  };

  const handleClickShowPassword = () => {
    setLoginForm({
      ...loginForm,
      showPassword: !loginForm.showPassword,
    });
  };

  const handleMouseDownPassword = event => {
    event.preventDefault();
  };

  useEffect(() => {
    getUserIP().then(res => setUserIP(formatIPJSON(res)));
  }, []);

  // check if token already exists. If so, redirect.
  // TODO: also check if token is valid and not expired
  useEffect(() => {
    if (hasToken()) {
      const coachingRole = parseInt(getLocalValue("u_coaching_role"));
      const isFirstLogin = getLocalValue("first_login") === "1";
      const has_completed_security_questions = getLocalValue("has_completed_security_questions") === "1";

      const url = getLoginUrl(coachingRole, isFirstLogin, has_completed_security_questions);

      history.push(url);
    } else {
      clearTokenFromLocalStorage();
    }
  }, [history]);

  useEffect(() => {
    const autoLogin = async (id, otp) => {
      try {
        const res = await apiRequest.post(`/api/autoLogin`, {
          id: id,
          otp: otp
        })

        const data = res.data;
        setAutoOtp(data.otp)
        setUserID(data.uid);
        setShowOTPScreen(true)
        setHasCompletedSecurityQuestions(data.has_completed_security_questions);
      } catch (error) {
        console.log(error)
        addNotification(t(`error.${error.response.data.msg}`), "error")
      }

    }
    if (autoLoginId && autoLoginOtp) {
      autoLogin(autoLoginId, autoLoginOtp)
    }
  }, [autoLoginId, autoLoginOtp])

  // handle redirect from security questions reset page
  useEffect(() => {
    const state = location.state;
    if (state?.showOTPScreen && state?.userID) {
      setUserID(state.userID);
      setShowOTPScreen(true);
      if (state.isQRReset) {
        setIsQRReset(true);
        setQrResetAttempts(5);
      }
      history.replace("/", {});
    }
  }, []);

  const handleRescanQR = async () => {
    try {
      const response = await apiRequest.post(`/api/login`, {
        username: loginForm.username,
        password: loginForm.password,
        userIP,
        currentDomain: window.location.hostname,
      });
      const data = response.data;
      if (data.userQrCodeUrl) {
        setQrCodeUrl(data.userQrCodeUrl);
        setScanDone(false);
        setVerificationAttempted(false);
      }
    } catch (error) {
      addNotification(t("login.rescanFailed"), "error");
    }
  };

  const handleLogin = async () => {
    setIsLoading(true);
    try {
      const response = await apiRequest.post(`/api/login`, {
        username: loginForm.username,
        password: loginForm.password,
        userIP: userIP,
        currentDomain: window.location.hostname,
      });
      let data = response.data;
      // if login success (status != "fail")
      if (
        !(
          data.status &&
          (data.status === "fail" || data.status === "invalidDomain")
        )
      ) {
        // if successful login
        setUserID(data.uid ?? data.user_id);
        setHasCompletedSecurityQuestions(data.has_completed_security_questions);
        if (data.is_multifactor_authentication) {
          setShowOTPScreen(true);
          setIsLoading(false);
        } else if (data.isQRcode) {
          if (data.register_otp === "true") {
            setScanDone(true);
          }
          setQrCodeUrl(data.userQrCodeUrl);
          setSecurityResetToken(data.security_reset_token ?? null);
          setShowQRScreen(true);
          setIsLoading(false);
        } else {
          handleLoginSuccess(data, history);
        }
      } else if (data.status === "fail") {
        setNumberOfAvailalbeTries(prevTries => prevTries - 1);
        addNotification(t(data.msg), "error");
        setIsLoading(false);
      } else if (data.status === "invalidDomain") {
        addNotification(t(data.msg), "error");
        setIsLoading(false);
      }
    } catch (error) {
      addNotification(t("login.somethingWentWrong"), "error");
      setIsLoading(false);
    }
  };

  // resend OTP for the QR reset flow - calls dedicated endpoint, not the normal login resend
  const handleResendQRResetOTP = async () => {
    try {
      const response = await apiRequest.post(`/api/security-questions/resend-otp`, {
        uid: userID,
        userIP: userIP,
      });
      const data = response.data;
      if (data.status === "success") {
        addNotification(t("login.codeSent"), "success");
      } else {
        addNotification(t("login.resendFailed"), "error");
      }
    } catch (error) {
      addNotification(t("login.somethingWentWrong"), "error");
    }
  };

  const handleResendOTP = async () => {
    try {
      const response = await apiRequest.post(`/api/resendOtp`, {
        username: loginForm.username,
      });

      const data = response.data;
      if (data.status === "success") {
        addNotification(t("login.codeSent"), "success");
      } else {
        addNotification(t("login.resendFailed"), "error");
      }
    } catch (error) {
      addNotification(t("login.somethingWentWrong"), "error");
    }
  };

  const handleOtpSubmit = async value => {
    try {
      if (isQRReset) {
        // dedicated endpoint for QR reset OTP - does not log a login or issue a token
        const response = await apiRequest.post(`/api/security-questions/verify-otp`, {
          uid: userID,
          login_otp: value,
          userIP: userIP,
        });
        const data = response.data;
        if (data.status === "success") {
          // QR reset complete - authenticator wiped, drop back to login form with a message
          setShowOTPScreen(false);
          setIsQRReset(false);
          setUserID(null);
          setVerificationAttempted(false);
          setQrResetAttempts(5);
          setNumberOfAvailalbeTries(10);
          addNotification(t("login.authenticatorReset"), "success");
        } else if (data.locked) {
          // Locked - boot them back to login form entirely
          setShowOTPScreen(false);
          setIsQRReset(false);
          setUserID(null);
          setVerificationAttempted(false);
          setQrResetAttempts(5);
          addNotification(t("login.tooManyAttempts"), "error");
        } else {
          setVerificationAttempted(true);
          // Use server-authoritative count instead of decrementing locally
          if (data.attempts_left !== undefined) {
            setQrResetAttempts(data.attempts_left);
          }
          addNotification(t(data.msg), "error");
          setIsLoading(false);
        }
      } else {
        // normal email OTP login flow
        const response = await apiRequest.post(`/api/verifyOTP`, {
          uid: userID,
          login_otp: value,
          userIP: userIP,
        });
        let data = response.data;
        // if login success (status != "fail")
        if (!(data.status && data.status === "fail")) {
          handleLoginSuccess(data, history);
        } else if (data.status === "fail") {
          setVerificationAttempted(true);
          addNotification(t(data.msg), "error");
          if (data.loginTries) {
            setVerificationAttempted(true);
            setNumberOfAvailalbeTries(prevTries => prevTries - 1);
          }
          setIsLoading(false);
        }
      }
    } catch (error) {
      addNotification(t("login.somethingWentWrong"), "error");
      setIsLoading(false);
    }
  };

  const handleScanSumbit = async () => {
    setScanDone(true);
  };

  const handleQROtpSubmit = async value => {
    try {
      const response = await apiRequest.post(`/api/login`, {
        username: loginForm.username,
        password: loginForm.password,
        userIP: userIP,
        uid: userID,
        Qrotp: value,
        currentDomain: window.location.hostname,
      });
      let data = response.data;
      // if login success (status != "fail")
      if (!(data.status && data.status === "fail") && !data.isQRcode) {
        handleLoginSuccess(data, history);
      } else if (data.isQRcode) {
        setVerificationAttempted(true);
        addNotification(t("login.fillOtpProperly"), "error");
      } else if (data.status === "fail") {
        setVerificationAttempted(true);
        addNotification(t(data.msg), "error");
        if (data.loginTries) {
          setVerificationAttempted(true);
          setNumberOfAvailalbeTries(10 - data.loginTries);
        }
        setIsLoading(false);
      }
    } catch (error) {
      addNotification(t("login.somethingWentWrong"), "error");
      setIsLoading(false);
    }
  };

  const helpRequestButton = {
    position: "absolute",
    right: 0,
    top: "50%",
    transform: "translateY(-50%)",
    borderTopLeftRadius: "10px",
    borderBottomLeftRadius: "10px",
    backgroundColor: "#E35646",
    padding: "5px",
    paddingTop: "15px",
    paddingBottom: "15px",
    display: "flex",
    flexDirection: "column-reverse",
    alignItems: "center",
    color: "white",
    cursor: "pointer",
  };

  const helpIcon = {
    display: "block",
    transform: "rotate(-90deg)",
    marginTop: "8px",
    lineHeight: "0.3",
    color: "white",
  };

  const helpRequestLetter = {
    display: "block",
    transform: "rotate(-90deg)",
    margin: "4px",
    lineHeight: "0.2",
  };

  return (
    <div className={styles.pageWrapper}>
      {process.env.REACT_APP_SHOW_MAINTENANCE_BANNER === 'true' && <MaintenanceBanner />}

      <div className={styles.loginBlock}>
        <div className={styles.logo}>
          <FlashCoachLogo />
        </div>
        {!showOTPScreen && !showQRScreen && (
          <form onSubmit={(e) => {
            e.preventDefault();
            handleLogin()
          }}>
            <div className={styles.loginContent}>
              <div className={styles.loginContent__formControl}>
                <TextField
                  variant="outlined"
                  fullWidth
                  label="Username"
                  type="text"
                  value={loginForm.username}
                  onChange={handleLoginFormEventChange("username")}
                  autoComplete="username"
                />
              </div>
              <div className={styles.loginContent__formControl}>
                <PasswordField
                  autoComplete="current-password"
                  label="Password"
                  show={loginForm.showPassword}
                  value={loginForm.password}
                  onChange={handleLoginFormEventChange("password")}
                  onShowClick={handleClickShowPassword}
                  onShowMouseDown={handleMouseDownPassword}
                />
              </div>
              <div
                className={ClassNames(
                  styles.loginContent__formControl,
                  styles.loginContent__actions
                )}
              >
                <div className={styles.left}>
                  <FormControlLabel
                    className={styles.checkbox}
                    control={
                      <Checkbox
                        color="secondary"
                        checked={loginForm.remember}
                        onChange={e => {
                          handleLoginFormChange("remember")(!loginForm.remember);
                        }}
                        name="remember"
                      />
                    }
                    label="Remember me"
                  />
                </div>
                {/* TODO: link to forgot password page */}
                <div className={styles.right}>
                  <Link
                    to="/forgotPassword"
                    className={styles.loginForm__forgotPassword}
                  >
                    Forgot password
                  </Link>
                  <button
                    type="button"
                    className={styles.firstTimeLink}
                    onClick={() => setShowFirstTimeModal(true)}
                  >
                    {t("login.firstTimeLink")}
                  </button>
                </div>
              </div>
              <div
                className={ClassNames(
                  styles.loginContent__formControl,
                  styles.loginContent__submitBtnWrapper
                )}
              >
                <Button
                  color="primary"
                  style={{ backgroundColor: "#f5b019" }}
                  variant="contained"
                  fullWidth
                  isloading={isLoading ? 1 : 0}
                  type="submit"
                >
                  {"Sign in"}
                </Button>
              </div>
            </div>
          </form>
        )}
        {showOTPScreen && (
          <div className={styles.loginContent}>
            <div className={styles.loginContent__formControl}>
              <OTPInput
                numberOfAvailalbeTries={isQRReset ? qrResetAttempts : numberOfAvailalbeTries}
                handleOtpSubmit={value => {
                  handleOtpSubmit(value);
                }}
                verificationAttempted={verificationAttempted}
                getNewCode={isQRReset ? handleResendQRResetOTP : handleResendOTP}
                username={loginForm.username}
                autoOtp={autoOtp}
                flow={isQRReset ? "reset" : "login"}
              />
              <div style={{ padding: "10px 0" }}>
                <Link
                  to={"/"}
                  onClick={() => {
                    setShowOTPScreen(false);
                  }}
                  className={styles.backLink}
                >
                  Back to Login
                </Link>
              </div>
            </div>
          </div>
        )}
        {showQRScreen && (
          <div className={styles.loginContent}>
            {!scanDone ? (
              <QRInputDisplay
                handleScanSumbit={handleScanSumbit}
                qrCodeUrl={qrCodeUrl}
                handleRescanQR={handleRescanQR}
              />
            ) : (
              <div className={styles.loginContent__formControl}>
                <QRInput
                  numberOfAvailalbeTries={numberOfAvailalbeTries}
                  handleQROtpSubmit={value => {
                    handleQROtpSubmit(value);
                  }}
                  verificationAttempted={verificationAttempted}
                  handleRescanQR={handleRescanQR}
                />
              </div>
            )}
            {Number(hasCompletedSecurityQuestions) === 1 && securityResetToken && (
              <div
                style={{
                  padding: "10px 0",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Link
                  to={`/security-questions/reset?token=${securityResetToken}`}
                  className={styles.backLink}
                >
                  Lost access to your authenticator app?
                </Link>
              </div>
            )}
            <div
              style={{
                padding: "10px 0",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Link
                to={"/"}
                onClick={() => {
                  setVerificationAttempted(false);
                  setShowQRScreen(false);
                  setScanDone(false);
                }}
                className={styles.backLink}
              >
                Back to Login
              </Link>
            </div>
          </div>
        )}
      </div>
      <div
        style={helpRequestButton}
        onClick={() => {
          history.push("/help-request/welcome");
        }}
      >
        <HelpOutlineIcon style={helpIcon} />
        <span className={styles.helpText}>Help Request Form</span>
      </div>

      <div className={styles.footer}>
        <div className={styles.logo}>
          <TDCXLogo width={100} />
        </div>
        <div className={styles.copyright}>
          Copyright © 2021 TDCX. All Rights Reserved. | Version{" "}
          {packageJson.version}
        </div>
      </div>

      <FirstTimeLoginModal
        open={showFirstTimeModal}
        onClose={() => setShowFirstTimeModal(false)}
      />
    </div>
  );
}
